import React from 'react';
import { Card, Table, Row, Col, Badge, Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip as ChartTooltip,
  Title
} from 'chart.js';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './ScorecardOverview.css';

const clearCanvasPlugin = {
  id: 'clearCanvasBackground',
  beforeDraw: (chart) => {
    const ctx = chart.canvas.getContext('2d');
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#fff'; // or 'transparent' if you want transparency
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  }
};

ChartJS.register(BarElement, CategoryScale, LinearScale, Legend, ChartTooltip, Title,clearCanvasPlugin);

const getStatusBadge = (status) => {
  switch (status) {
    case 'Strong':
      return <Badge bg="success" className="fw-normal px-2 py-1">Strong</Badge>;
    case 'Improving':
      return <Badge bg="info" className="fw-normal px-2 py-1">Improving</Badge>;
    case 'Stable':
      return <Badge bg="secondary" className="fw-normal px-2 py-1">Stable</Badge>;
    case 'Needs Work':
      return <Badge bg="danger" className="fw-normal px-2 py-1">Needs Work</Badge>;
    default:
      return <Badge bg="light" className="fw-normal px-2 py-1">Unknown</Badge>;
  }
};

const getStatus = (score) => {
  const num = parseFloat(score);
  if (num >= 80) return 'Strong';
  if (num >= 70) return 'Improving';
  if (num >= 60) return 'Stable';
  return 'Needs Work';
};

const ScorecardOverview = ({
  unitData = [],
  overallThemeSummary = {},
  selectedPeriod = '2025'
}) => {
  const calculateScore = (theme) => {
    const { total, reached } = overallThemeSummary[theme] || {};
    const percentage = total ? ((reached / total) * 100).toFixed(0) : '0';
    const delta = reached || 0;
    return { percentage, delta };
  };

  const environment = calculateScore('Environment');
  const social = calculateScore('Social');
  const governance = calculateScore('Governance');

  const chartData = {
    labels: unitData.map((unit) => unit.unitName),
    datasets: [
      {
        label: 'Environment',
        data: unitData.map((u) => parseFloat(u.Environment.percentage)),
        backgroundColor: 'rgba(76, 175, 80, 0.8)',
        borderColor: '#388e3c',
        borderWidth: 1,
      },
      {
        label: 'Social',
        data: unitData.map((u) => parseFloat(u.Social.percentage)),
        backgroundColor: 'rgba(33, 150, 243, 0.8)',
        borderColor: '#1976d2',
        borderWidth: 1,
      },
      {
        label: 'Governance',
        data: unitData.map((u) => parseFloat(u.Governance.percentage)),
        backgroundColor: 'rgba(255, 193, 7, 0.8)',
        borderColor: '#ffa000',
        borderWidth: 1,
      },
    ],
  };


 


  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 15,
          usePointStyle: true,
          pointStyle: 'rectRounded'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: 13, weight: 'bold' },
        bodyFont: { size: 12 },
        padding: 10,
        cornerRadius: 4
      },
    },
    // scales: {
    //   y: {
    //     beginAtZero: true,
    //   grid: { color: 'rgba(0, 0, 0, 0.04)' },
    //   ticks: { font: { size: 10 } },
    //   stacked: false
    //   },
    //   x: {
    //     grid: { display: false },
    //   ticks: { font: { size: 10 } },
    //   stacked: false
    //   }
    // }
    scales: {
  y: {
    beginAtZero: true,
    grid: {
      drawTicks: true,
      drawBorder: true,
      color: 'rgba(0,0,0,0.05)',   // ← optional light grid lines
      backgroundColor: 'transparent', // ← this won't work directly here in Chart.js v3
    },
    ticks: { font: { size: 10 } },
    stacked: false,
  },
  x: {
    grid: { display: false },
    ticks: { font: { size: 10 } },
    stacked: false,
  },
},

  };

  const overallAverage = unitData.length > 0
    ? (unitData.reduce((sum, unit) => sum + parseFloat(unit.overallPercentage), 0) / unitData.length).toFixed(1)
    : 0;

  // Helper to render info icon with tooltip
  const renderInfoIcon = (theme) => {
    const summary = overallThemeSummary[theme] || {};
    const reached = summary.reached || 0;
    const total = summary.total || 0;
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`tooltip-${theme}`}>
            {`${reached} out of ${total} KPIs reached`}
          </Tooltip>
        }
      >
        <i className="fas fa-info-circle ms-1 text-secondary" style={{ cursor: 'pointer', fontSize: '1rem', verticalAlign: 'middle' }}></i>
      </OverlayTrigger>
    );
  };

 

  return (
    <Container className="py-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="fw-semibold mb-1">ESG Scorecard Overview</h5>
          <small className="text-muted">Reporting Period: <strong>{selectedPeriod}</strong></small>
        </div>
        <div>
          <h6 className="mb-0">
            Overall Avg: <span className={`fw-bold ${overallAverage >= 70 ? 'text-success' : overallAverage >= 60 ? 'text-warning' : 'text-danger'}`}>{overallAverage}%</span>
          </h6>
        </div>
      </div>

      {/* Score Cards */}
      <Row className="g-3 mb-3">
        <Col md={4}>
          <div className="d-flex align-items-center p-3 rounded-3 shadow-sm" style={{ backgroundColor: '#d4edda' }}>
            <i className="fas fa-recycle fa-lg me-3" style={{ color: '#2e7d32' }}></i>
            <div>
              <div className="fw-semibold" style={{ color: '#2e7d32' }}>Environmental Score</div>
              <div className="fw-bold fs-5" style={{ color: '#2e7d32' }}>
                {environment.percentage} / 100 <span className="ms-2">+{environment.delta}</span> {renderInfoIcon('Environment')}
              </div>
            </div>
          </div>
        </Col>
        <Col md={4}>
          <div className="d-flex align-items-center p-3 rounded-3 shadow-sm" style={{ backgroundColor: '#e3f2fd' }}>
            <i className="fas fa-users fa-lg me-3" style={{ color: '#1565c0' }}></i>
            <div>
              <div className="fw-semibold" style={{ color: '#1565c0' }}>Social Score</div>
              <div className="fw-bold fs-5" style={{ color: '#1565c0' }}>
                {social.percentage} / 100 <span className="ms-2">+{social.delta}</span> {renderInfoIcon('Social')}
              </div>
            </div>
          </div>
        </Col>
        <Col md={4}>
          <div className="d-flex align-items-center p-3 rounded-3 shadow-sm" style={{ backgroundColor: '#fff3cd' }}>
            <i className="fas fa-university fa-lg me-3" style={{ color: '#8d6e63' }}></i>
            <div>
              <div className="fw-semibold" style={{ color: '#8d6e63' }}>Governance Score</div>
              <div className="fw-bold fs-5" style={{ color: '#8d6e63' }}>
                {governance.percentage} / 100 <span className="ms-2">-{governance.delta}</span> {renderInfoIcon('Governance')}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Performance Table */}
      <Card className="border-0 shadow-sm rounded-3 mb-3">
        <Card.Body className="p-3">
          <h6 className="fw-semibold mb-3">Unit Performance</h6>
          <div className="table-responsive">
            <Table size="sm" className="mb-0 small">
              <thead className="bg-light">
                <tr>
                  <th>Unit</th>
                  <th className="text-center">Env</th>
                  <th className="text-center">Soc</th>
                  <th className="text-center">Gov</th>
                  <th className="text-center">Overall</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {unitData.map((unit, idx) => {
                  const status = getStatus(unit.overallPercentage);
                  return (
                    <tr key={idx}>
                      <td>{unit.unitName}</td>
                      <td className="text-center text-success">{unit.Environment.percentage}%</td>
                      <td className="text-center text-primary">{unit.Social.percentage}%</td>
                      <td className="text-center text-warning">{unit.Governance.percentage}%</td>
                      <td className="text-center fw-bold">{unit.overallPercentage}%</td>
                      <td className="text-center">{getStatusBadge(status)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Chart */}
      <Card className="border-0 shadow-sm rounded-3">
        <Card.Body className="p-3">
          <h6 className="fw-semibold mb-3">Theme-wise Comparison</h6>
          <div style={{ height: 220, backgroundColor: 'transparent' }}>
            <Bar data={chartData} options={chartOptions} />
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ScorecardOverview;



// const fetchAllUnitData = async () => {
//   if (!user?.id || !token) return;
//   setLoading(true);

//   try {
//     const unitRes = await fetch(`${process.env.REACT_APP_BASE_URL}assign-unit/getAll`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${token}`,
//       },
//     });

//     const unitResult = await unitRes.json();

//     const filteredUnits = unitResult.data.filter(
//       (unit) => unit.companyId === user?.id
//     );

//     if (!unitResult.status) return;

//     const units = filteredUnits;
//     const summaryList = [];

//     for (const unit of units) {
//       const unitId = String(unit.id);
//       const unitName = unit.name || unitId;

//       const fieldsRes = await fetch(`${process.env.REACT_APP_BASE_URL}assign-companies/assignFields/scorecard/${user.id}`, {
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${token}`,
//         },
//       });

//       const fieldsResult = await fieldsRes.json();
//       if (!fieldsResult.status) continue;

//       const assignedFields = fieldsResult.data;
//       const fieldIds = assignedFields.map(f => String(f.fieldId));

//       const baseYear = String(Number(year) - 1);
//       const baseDataResponse = await fetch(`${process.env.REACT_APP_BASE_URL}scorecard/fetch-all`, {
//         method: "POST",
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${token}`,
//         },
//         body: JSON.stringify({
//           fieldIds,
//           companyUnit: unitId,
//           userId: String(user.id),
//           selectedYear: baseYear,
//         }),
//       });
//       const baseYearResult = await baseDataResponse.json();
//       const baseYearData = baseYearResult.data || {};
//       const govBaseRes = await fetch(`${process.env.REACT_APP_BASE_URL}governance/getGovernanceData`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${token}`,
//         },
//         body: JSON.stringify({
//           kpiId: assignedFields.map(f => f.kpiId),
//           catId: assignedFields.map(f => f.catId),
//           subCatId: assignedFields.map(f => f.subCatId),
//           fieldId: assignedFields.map(f => f.fieldId),
//           companyId: assignedFields.map(f => f.companyId),
//           companyUnit: [unitId],
//           year: [baseYear],
//         }),
//       });
//       const govBaseResult = await govBaseRes.json();
//       baseYearData.governance = govBaseResult.data || [];
//       const socBaseRes = await fetch(`${process.env.REACT_APP_BASE_URL}social/getSocialData`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${token}`,
//         },
//         body: JSON.stringify({
//           kpiId: assignedFields.map(f => f.kpiId),
//           catId: assignedFields.map(f => f.catId),
//           subCatId: assignedFields.map(f => f.subCatId),
//           fieldId: assignedFields.map(f => f.fieldId),
//           companyId: assignedFields.map(f => f.companyId),
//           companyUnit: [unitId],
//           year: [baseYear],
//         }),
//       });
//       const socBaseResult = await socBaseRes.json();
//       baseYearData.social = socBaseResult.data || [];
//       const { targets: unitTargetMap, fieldStatusMapping: unitStatusMap } = calculateFieldTargets(assignedFields, baseYearData);

//       const scoreRes = await fetch(`${process.env.REACT_APP_BASE_URL}scorecard/fetch-all`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${token}`,
//         },
//         body: JSON.stringify({
//           fieldIds,
//           companyUnit: unitId,
//           userId: String(user.id),
//           selectedYear: String(year),
//         }),
//       });

//       const scoreResult = await scoreRes.json();
//       const scorecardData = scoreResult.data || {};

//       const filteredScorecardData = ['water', 'waste', 'emission', 'energy'].reduce((acc, type) => {
//         const items = scorecardData[type] || [];
//         if (frequency === "Monthly" && month !== "All") {
//           acc[type] = items.filter(i => i.frequencyMonth === month);
//         } else {
//           acc[type] = aggregateYearlyData(items, year);
//         }
//         return acc;
//       }, {});

//       const themeCounts = {
//         Environment: { total: 0, reached: 0 },
//         Governance: { total: 0, reached: 0 },
//         Social: { total: 0, reached: 0 },
//       };

//       ['water', 'waste', 'emission', 'energy'].forEach(dataset => {
//         const datasetItems = filteredScorecardData[dataset] || [];

//         if (dataset === "water") {
//           const groupAItems = datasetItems.filter(i => i.fieldGroup === "A");
          
//           if (groupAItems.length > 0) {
//             const total = groupAItems.reduce((sum, i) => {
//               const value = Number(i.prevValue || 0);
//               return sum + value;
//             }, 0);
            
//             const targets = groupAItems.map(i => unitTargetMap[i.fieldId]).filter(Boolean);
//             const targetSum = targets.reduce((a, b) => a + b, 0);
            
//             themeCounts.Environment.total++;
//             if (total <= targetSum) {
//               themeCounts.Environment.reached++;
//             } 
//           }
//         }
        
//         if (dataset === "waste") {
//           const groupBItems = datasetItems.filter(i => ["B1", "B2", "B3"].includes(i.fieldGroup));
//           if (groupBItems.length > 0) {
//             const total = groupBItems.reduce((sum, i) => sum + Number(i.prevValue || 0), 0);
//             const targets = groupBItems.map(i => unitTargetMap[i.fieldId]).filter(Boolean);
//             const targetSum = targets.reduce((a, b) => a + b, 0);
        
//             themeCounts.Environment.total++;
//             if (total >= targetSum) {
//               themeCounts.Environment.reached++;
//             }
//           }
//         }

//         datasetItems.forEach(item => {
//           if ((dataset === "water" && item.fieldGroup === "A") ||
//               (dataset === "waste" && ["B1", "B2", "B3"].includes(item.fieldGroup))) return;

//           const selected = unitStatusMap[item.fieldId] === "isLeading" ? "leading"
//             : unitStatusMap[item.fieldId] === "isLagging" ? "lagging" : "-";
//           const current = Number(item.prevValue || 0);
//           const target = Number(unitTargetMap[item.fieldId]);

//           if (selected === "-" || isNaN(target)) return;

//           themeCounts.Environment.total++;
//           if ((selected === "leading" && current >= target) ||
//             (selected === "lagging" && current < target)) {
//             themeCounts.Environment.reached++;
//           }
//         });
//       });

//       const sharedFilter = {
//         fieldId: fieldIds,
//         companyUnit: [unitId],
